/**
 * supportIA en modo Agente TA Metrics — el cuerpo del drawer cuando la persona
 * está en People → Talent Acquisition (mismo mecanismo que PartnershipsChatMode
 * con el Hub de Partnerships). Responde solo sobre la "TA Metrics - Base agente"
 * y siempre en español (RF-16): la UI se traduce, las respuestas no.
 *
 * Los turnos van por la Function URL directa (VITE_TA_ASK_URL, sin el timeout
 * de 30 s de CloudFront); si no está configurada, por tRPC. El estado vive en
 * ta-chat-store, pero este bot NO persiste la conversación: cada apertura del
 * drawer arranca limpia (pedido de Sol, 17/09/2026; los demás bots sí persisten).
 */

import { ChatComposer } from '@/components/chat/ChatComposer';
import { useChatScroll } from '@/components/chat/useChatScroll';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, FileDown, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/SharedAuthContext';
import { BrainMarkdown } from '@/components/brain/BrainMarkdown';
import { Button } from '@/components/ui/button';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTaReporteBusqueda } from '@/hooks/useTalentAcquisition';
import { trpc } from '@/lib/trpc';
import { askTaAgentDirect, TA_ASK_URL, type TaAskResult } from '@/lib/ta-api';
import { normalizeLang } from '@/../../shared/lang';
import { cn } from '@/lib/utils';
import { useTaSuggestedQuestions, type TaAssistantScope } from './ta-assistant-questions';
import {
  getTaChatSession, getTaChatSnapshot, resetTaChat, setTaChatMessages, setTaChatPending, subscribeTaChat, type TaChatMessage,
} from './ta-chat-store';

/** Advertencias del validador que valen para la persona (las de estilo quedan solo en el audit). */
const userFacingIssues = (issues: string[]) => issues.filter((i) => !/^respuesta larga/.test(i));

export function TaChatMode({ initialQuestion, scope = 'metrics' }: { initialQuestion?: string; scope?: TaAssistantScope }) {
  const { t, i18n } = useTranslation();
  const preguntas = useTaSuggestedQuestions(scope);
  const uiLang = normalizeLang(i18n.language);
  // Mismo hook que el botón del detalle de la Hiring Plan: genera el PDF y lo abre
  const pdf = useTaReporteBusqueda();
  const k = 'talent_acquisition.assistant';
  const { user } = useAuth();
  const email = user?.email;
  // Cada apertura del drawer es una sesión nueva y vacía. Sincrónico y sin
  // emitir, una sola vez por montaje: el snapshot que lee useSyncExternalStore
  // en este mismo render ya está limpio.
  const opened = useRef(false);
  if (!opened.current) { opened.current = true; resetTaChat(email); }
  const { messages, pending } = useSyncExternalStore(subscribeTaChat, getTaChatSnapshot);
  const [input, setInput] = useState('');
  const initialSent = useRef(false);
  // El apartado de intake pregunta por lo propio: otra audiencia (canIntake) y otro universo.
  const askTrpc = trpc.taMetrics.ask.useMutation();
  const askMineTrpc = trpc.taIntake.askMySearches.useMutation();
  const bottomRef = useChatScroll([messages, pending]);

  const send = async (text?: string) => {
    const question = (text ?? input).trim();
    if (!question) return;
    // Hay una respuesta en vuelo: la pregunta clickeada no se pierde, queda en el input
    if (getTaChatSnapshot().pending) { setInput(question); return; }
    setInput('');
    // Firma de la sesión: si el drawer se cierra y reabre mientras el agente
    // calcula, la respuesta vieja no cae en el chat nuevo (que arranca limpio).
    const sess = getTaChatSession();
    const before = getTaChatSnapshot().messages;
    const history = before.slice(-10).map((m) => ({ role: m.role, content: m.text }));
    setTaChatMessages(sess, [...before, { role: 'user', text: question }]);
    setTaChatPending(sess, true);
    try {
      const result: TaAskResult = TA_ASK_URL
        ? await askTaAgentDirect({ question, history, lang: uiLang, scope: scope === 'intake' ? 'mine' : 'all' })
        : scope === 'intake'
          ? await askMineTrpc.mutateAsync({ question, history, lang: uiLang })
          : await askTrpc.mutateAsync({ question, history, lang: uiLang });
      const reply: TaChatMessage = {
        role: 'assistant',
        text: result.reply,
        meta: { latencyMs: result.latencyMs, downloadedAt: result.dataset.downloadedAt, hoy: result.dataset.hoy, issues: result.validation.issues },
        ...(result.attachment ? { attachment: result.attachment } : {}),
      };
      setTaChatMessages(sess, [...getTaChatSnapshot().messages, reply]);
    } catch (e) {
      if (sess !== getTaChatSession()) return; // el drawer ya se cerró: no hay a quién avisar
      toast.error(t(`${k}.error`, { error: e instanceof Error ? e.message : String(e) }));
      // La pregunta vuelve al input para reintentar
      const cur = getTaChatSnapshot().messages;
      const last = cur[cur.length - 1];
      setTaChatMessages(sess, last?.role === 'user' && last.text === question ? cur.slice(0, -1) : cur);
      setInput(question);
    } finally {
      setTaChatPending(sess, false);
    }
  };

  // Pregunta clickeada en la burbuja del FAB: se manda una sola vez
  useEffect(() => {
    if (initialQuestion && !initialSent.current) {
      initialSent.current = true;
      void send(initialQuestion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion]);

  return (
    <>
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        <p className="text-xs text-muted-foreground">{t(`${k}.intro`)}</p>
        {messages.length === 0 && (
          <div className="mt-2 space-y-2">
            <p className="text-xs text-muted-foreground">{t('assistant.assistantchatdrawer.preguntas_frecuentes')}</p>
            {preguntas.map((q) => (
              <button key={q} onClick={() => send(q)} className="w-full rounded-lg border border-muted-foreground/20 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted">
                {q}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div className={cn('max-w-[92%] rounded-lg px-3 py-2 text-sm', m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground')}>
              {m.role === 'user' ? (
                <p className="whitespace-pre-wrap">{m.text}</p>
              ) : (
                <div className="prose prose-sm max-w-none dark:prose-invert [&_table]:text-xs">
                  <BrainMarkdown content={m.text} />
                </div>
              )}
              {/* §18: el PDF se descarga con este botón — el agente no escribe links
                  (una URL prefirmada le comía la respuesta entera, prod 22/09). */}
              {m.attachment?.kind === 'ta_report' && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2 h-8 gap-1.5"
                  disabled={pdf.isPending}
                  onClick={() => pdf.mutate({ ID: m.attachment!.ID, origen: 'chat' })}
                >
                  {pdf.isPending ? <WinclapLoader size={14} /> : <FileDown className="h-3.5 w-3.5" />}
                  {pdf.isPending
                    ? t('talent_acquisition.hiring_plan.detail.pdf_generando')
                    : t(`${k}.download_pdf`, { posicion: m.attachment.posicion, id: m.attachment.ID })}
                </Button>
              )}
              {m.meta && (
                <p className="mt-1.5 text-[10px] text-muted-foreground">
                  {t(`${k}.meta`, { seconds: (m.meta.latencyMs / 1000).toFixed(1), today: m.meta.hoy })}
                </p>
              )}
              {/* El guard-rail anti-alucinación no es write-only: si marcó algo, la persona lo ve */}
              {m.meta && userFacingIssues(m.meta.issues).length > 0 && (
                <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>{t(`${k}.validation_warning`, { issues: userFacingIssues(m.meta.issues).join(' · ') })}</span>
                </p>
              )}
            </div>
          </div>
        ))}
        {pending && (
          <div className="flex justify-start">
            <div className="animate-pulse rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">{t(`${k}.thinking`)}</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="border-t p-4">
        <ChatComposer value={input} onChange={setInput} onSend={() => void send()} placeholder={t(`${k}.placeholder`)} maxLength={2000} disabled={pending} />
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><RefreshCw className="h-3 w-3" />{t(`${k}.refresh_hint`)}</span>
          {messages.length > 0 && !pending && (
            <button onClick={() => setTaChatMessages(getTaChatSession(), [])} className="flex items-center gap-1 hover:text-foreground" title={t(`${k}.clear`)}>
              <Trash2 className="h-3 w-3" />{t(`${k}.clear`)}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
